import React, { ReactElement } from 'react'
import styled from 'styled-components'
import { RADOM_COLORS } from '../util/Constants'
import Tooltip from './Tooltip'
import { VerificationRequirementError } from './VerificationRequirementError'

export const IconButtonInner = styled.div`
  display: flex;
  gap: 8px;
  font-size: 12px;
  align-items: center;
  justify-content: center;
`

export const BaseInputStyles = `
  border: 1px solid var(--border-strong, #d8dde7);
  background-color: var(--surface, #ffffff);
  border-radius: 12px;
  box-sizing: border-box;
  min-height: 44px;
  padding: 9px 12px;
  width: 100%;
  outline: none;
  transition: 0.18s ease all;
  color: var(--ink, #20242d);
  font-size: 14px;
  font-weight: 400;
  letter-spacing: -0.01em;
  line-height: 1.35;

  &:hover {
    background-color: var(--surface, #ffffff);
    border-color: var(--border-strong, #bfc6d2);
  }

  :focus {
    border: 1px solid var(--focus-border, rgba(17, 17, 17, 0.22));
    background-color: var(--surface, #ffffff);
    box-shadow: 0 0 0 3px var(--focus-ring, rgba(17, 17, 17, 0.05));
  }

  ::placeholder {
    color: ${RADOM_COLORS.GRAY_DARKER};
  }
`

export const TextArea = styled.textarea<{ disabled?: boolean }>`
  ${BaseInputStyles}
  border-radius: 10px;
  max-width: 100%;
  width: 100%;
  box-sizing: border-box;
  resize: vertical;
  min-height: 50px;
`

export const OptionalLabel = styled.div`
  max-width: min-content;
  background-color: var(--surface-pressed, #f3f4f6);
  border-radius: 999px;
  padding: 3px 8px;
  color: ${RADOM_COLORS.GRAY_DARKEST};
  font-size: 10px;
  user-select: none;
`

interface TextAreaWithLabelProps extends React.HTMLProps<HTMLTextAreaElement> {
  errorMessage?: string
}

export const TextAreaWithLabel = (
  { label, value, onChange, placeholder, onKeyDown, required, disabled, errorMessage }: TextAreaWithLabelProps
): ReactElement => {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 5 }}>
      <InputLabel>
        <span>{ label }</span>
      </InputLabel>
      {!required && <OptionalLabel>Optional</OptionalLabel>}
    </div>
    {errorMessage && <VerificationRequirementError message={errorMessage} />}
    <TextArea
      style={{ maxHeight: 100 }}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      onKeyDown={onKeyDown}
      required={required}
      disabled={disabled}
    />
  </div>
}

interface TextWithLabelProps extends React.HTMLProps<HTMLInputElement> {
  tooltip?: string
  errorMessage?: string
}

export const TextInputWithLabel = (
  {
    label,
    tooltip,
    value,
    onChange,
    placeholder,
    pattern,
    onKeyDown,
    required,
    disabled,
    ref,
    as,
    style,
    errorMessage,
    ...props
  }: TextWithLabelProps
): ReactElement => {
  return <div style={{ display: 'flex', flexDirection: 'column', gap: 10, ...style }}>
    <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap', gap: 5 }}>
      <InputLabel>
        <span>{ label }</span>
      </InputLabel>
      {!required && <OptionalLabel>Optional</OptionalLabel>}
      {tooltip && <Tooltip message={tooltip} /> }
    </div>
    {errorMessage && <VerificationRequirementError message={errorMessage} />}
    <TextInput
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      pattern={pattern}
      onKeyDown={onKeyDown}
      disabled={disabled}
      required={required}
      {...props}
    />
  </div>
}

export const InputLabel = styled.span`
  font-weight: 410;
  display: flex;
  flex-direction: row;
  gap: 8px;
  align-items: center;
  color: var(--text-muted, #717887);
  font-size: 12.75px;
  line-height: 1.35;
`

export const TextInput = styled.input<{ pattern?: string, disabled?: boolean }>`
  ${BaseInputStyles}

  ${({ pattern }) => pattern && `
    :invalid {
      border: 1px solid ${RADOM_COLORS.ERROR};
    }`
}
`

export const InputWithCheckbox = styled.div`
  padding: 6px;
  display: flex;
  gap: 2px;
  border: 1px solid ${RADOM_COLORS.GRAY_DARK};
  border-radius: 4px;
  
  :focus-within {
    outline: 1px solid ${RADOM_COLORS.GRAY_MED};
  }
`

export const UnstyledInput = styled.input`
  border: 0;
  width: 100%;
  
  :focus {
    outline: none;
    border: none;
  }
`
